import type { RefObject, SubmitEventHandler } from 'react';
import type { SetFormApiFn } from '../../form-hooks/form-components/FormStateHandler/FormStateHandler';
import type { Space } from '../../lib/flotiq-client/types/api-response';

import { useCallback, useMemo, useReducer } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import moment from 'moment';
import z from 'zod';

import {
  setFormSubmitErrors,
  showValidationErrorToast,
} from '../../form-hooks/lib/helpers';
import { getStripeURL, getTestProps } from '../../lib/helpers';

import Button from '../../components/Button/Button';
import { useGridNavigate } from '../../components/DataGrid/useGridFilters';
import Heading from '../../components/Heading/Heading';
import LinkButton from '../../components/LinkButton/LinkButton';

import { useAppForm } from '../../form-hooks/form';

const PAYMENT_STATUS_OPTIONS = [
  { label: 'paid', value: 'paid' },
  { label: 'non-paid', value: 'non-paid' },
  { label: 'trial', value: 'trial' },
];

const PAYMENT_INTERVAL_OPTIONS = [
  { label: 'month', value: 'month' },
  { label: 'year', value: 'year' },
];

type SpaceAdminFormValue = Pick<
  Space,
  | 'name'
  | 'paymentStatus'
  | 'paymentInterval'
  | 'paymentDueDate'
  | 'subscriptionId'
>;

export type SpaceAdminFormSubmitFn = (
  value: SpaceAdminFormValue,
) => Promise<
  [[SpaceAdminFormValue, Record<string, string | string[]>], boolean]
>;

interface SpaceAdminFormProps {
  /**
   * On submit callback
   */
  onSubmit: SpaceAdminFormSubmitFn;
  /**
   * Space to edit
   */
  space?: Space | null;
  /**
   * If form is disabled
   */
  disabled?: boolean;
  /**
   * If navigates after submit represented by react ref
   */
  navigateOnSave?: RefObject<boolean>;
  /**
   * Set form state
   */
  setFormState?: SetFormApiFn;
  /**
   * Test id for page
   */
  testId?: string;
}

const getInitialValues = (space?: Space | null) => {
  return {
    name: space?.name || '',
    paymentStatus: space?.paymentStatus || 'non-paid',
    paymentInterval: space?.paymentInterval || null,
    paymentDueDate: space?.paymentDueDate
      ? moment(space?.paymentDueDate).format('YYYY-MM-DDTHH:mm')
      : '',
    subscriptionId: space?.subscriptionId || '',
  };
};

const SpaceAdminForm = ({
  space,
  onSubmit,
  disabled = false,
  navigateOnSave,
  setFormState,
  testId = '',
}: SpaceAdminFormProps) => {
  const { t } = useTranslation();
  const { navigateGrid } = useGridNavigate('spaces', '/spaces-data-preview');

  const [enabledAdminFieldsEdit, toggleEnabledAdminFieldsEdit] = useReducer(
    (enable) => !enable,
    false,
  );

  const validationSchema = useMemo(
    () =>
      z.object({
        name: z.string().min(1, t('Form.FormErrorNotBlank')),
        paymentStatus: z.string(),
        paymentInterval: z.string(),
        paymentDueDate: z.string(),
        subscriptionId: z.string(),
      }),
    [t],
  );

  const stripeURL = getStripeURL(
    `/subscriptions/${space?.subscriptionId || ''}`,
  );

  const form = useAppForm({
    defaultValues: getInitialValues(space),
    onSubmit: async ({ value, formApi }) => {
      const [[newValues, errors], hasErrors] = await onSubmit(value);

      if (!hasErrors) {
        if (navigateOnSave?.current) {
          navigateGrid();
        }
        formApi.reset(newValues);
      } else {
        const filteredErrors = setFormSubmitErrors(formApi, errors);
        showValidationErrorToast(filteredErrors);
      }
    },
    validators: {
      onChange: validationSchema,
    },
  });

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  return (
    <form.AppForm>
      <form
        id="space-form"
        className="space-y-2 md:space-y-4 p-5 md:pt-10 md:px-12"
        onSubmit={handleSubmit}
        noValidate
      >
        <div className="flex flex-col items-left gap-3 w-full">
          <form.AppField name="name">
            {(field) => (
              <field.Input
                label={t('Global.Name')}
                disabled={disabled}
                required
                {...getTestProps(testId, 'space-name', 'testId')}
              />
            )}
          </form.AppField>

          <div>
            <Heading level={3} additionalClasses="dark:text-slate-200">
              {t('Users.PaymentDetails')}
            </Heading>
            <p className="text-sm dark:text-slate-200">
              {t('Users.PaymentDetailsUpdateAlert')}
            </p>
          </div>
          <Button
            buttonColor="grayBordered"
            onClick={toggleEnabledAdminFieldsEdit}
            disabled={disabled}
            additionalClasses="w-fit mb-2"
            {...getTestProps(testId, 'toggle-payment-edit', 'testId')}
          >
            {t('Users.ToggleEdit')}
          </Button>

          <form.AppField name="paymentStatus">
            {(field) => (
              <field.Dropdown
                label={t('Users.PaymentStatus')}
                disabled={!enabledAdminFieldsEdit || disabled}
                options={PAYMENT_STATUS_OPTIONS}
                {...getTestProps(testId, 'space-paymentStatus', 'testId')}
              />
            )}
          </form.AppField>

          <form.AppField name="paymentInterval">
            {(field) => (
              <field.Dropdown
                label={t('Users.PaymentInterval')}
                disabled={!enabledAdminFieldsEdit || disabled}
                options={PAYMENT_INTERVAL_OPTIONS}
                nullable
                {...getTestProps(testId, 'space-paymentInterval', 'testId')}
              />
            )}
          </form.AppField>

          <form.AppField name="paymentDueDate">
            {(field) => (
              <field.Input
                label={t('Users.PaymentDueDate')}
                type="datetime-local"
                disabled={!enabledAdminFieldsEdit || disabled}
                {...getTestProps(testId, 'space-paymentDueDate', 'testId')}
              />
            )}
          </form.AppField>

          {space && stripeURL && (
            <form.AppField name="subscriptionId">
              {(field) => (
                <field.Input
                  label={t('Spaces.SubscriptionId')}
                  disabled={!enabledAdminFieldsEdit || disabled}
                  helpText={
                    <div className="mt-1">
                      <Trans i18nKey="Spaces.SubscriptionLink">
                        See
                        <LinkButton
                          link={stripeURL}
                          target="_blank"
                          rel="noreferrer"
                          buttonSize={'sm'}
                          buttonColor={'borderless'}
                          additionalClasses="inline font-normal"
                        >
                          Stripe subscription
                        </LinkButton>
                      </Trans>
                    </div>
                  }
                  {...getTestProps(testId, 'space-subscriptionId', 'testId')}
                />
              )}
            </form.AppField>
          )}
        </div>

        <form.DirtyHandler />
        <form.ValidationToastHandler />
        <form.FormStateHandler setFormState={setFormState} />
      </form>
    </form.AppForm>
  );
};

export default SpaceAdminForm;
